import React from "react";
import { clsx } from "clsx";
import { Skeleton } from "../Skeleton";

export interface TableColumn<T> {
    key: string;
    header: React.ReactNode;
    render?: (item: T) => React.ReactNode;
    className?: string;
}

export type TableViewMode = "grid" | "list" | "table";

export interface TableProps<T> {
    data: T[];
    columns: TableColumn<T>[];
    isLoading?: boolean;
    onRowClick?: (item: T) => void;
    className?: string;
    emptyMessage?: string;
    /** Current display mode. Defaults to "table". */
    viewMode?: TableViewMode;
    /** Custom card renderer for grid mode */
    renderGridItem?: (item: T) => React.ReactNode;
    /** Custom row renderer for list mode */
    renderListItem?: (item: T) => React.ReactNode;
}

export function Table<T extends { id: number | string }>({
    data,
    columns,
    isLoading = false,
    onRowClick,
    className,
    emptyMessage = "No data available",
    viewMode = "table",
    renderGridItem,
    renderListItem,
}: TableProps<T>) {
    // -------------------------------------------------------------
    // LOADING STATES
    // -------------------------------------------------------------
    if (isLoading) {
        if (viewMode === "grid") {
            return (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div
                            key={i}
                            className="flex flex-col justify-between rounded-2xl border border-neutral-border/60 bg-white p-5 animate-pulse dark:border-white/10 dark:bg-neutral-navy"
                        >
                            <div className="space-y-3">
                                <div className="flex items-center justify-between border-b border-neutral-border/40 pb-3 dark:border-white/10">
                                    <Skeleton height={18} width="60%" />
                                    <Skeleton height={20} width="20%" />
                                </div>
                                <div className="grid grid-cols-2 gap-3 pt-1">
                                    <div className="space-y-1">
                                        <Skeleton height={10} width="40%" />
                                        <Skeleton height={14} width="70%" />
                                    </div>
                                    <div className="space-y-1">
                                        <Skeleton height={10} width="40%" />
                                        <Skeleton height={14} width="70%" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            );
        }

        if (viewMode === "list") {
            return (
                <div className="space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                        <div
                            key={i}
                            className="flex items-center justify-between rounded-xl border border-neutral-border/60 bg-white p-4 animate-pulse dark:border-white/10 dark:bg-neutral-navy"
                        >
                            <div className="flex flex-1 items-center gap-6">
                                <Skeleton height={18} width="25%" />
                                <Skeleton height={14} width="20%" />
                                <Skeleton height={14} width="20%" />
                            </div>
                            <Skeleton height={28} width={28} />
                        </div>
                    ))}
                </div>
            );
        }

        return (
            <div className="w-full overflow-hidden rounded-mz-xl border border-neutral-border/60 dark:border-white/10">
                <div className="max-w-full overflow-x-auto">
                    <table className={clsx("w-full table-auto border-collapse", className)}>
                        <thead>
                            <tr className="border-b border-neutral-border bg-neutral-light/50 dark:border-white/10 dark:bg-black/20">
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className={clsx(
                                            "min-w-0 px-4 py-3 text-start text-sm font-semibold text-neutral-dark dark:text-white",
                                            column.className,
                                        )}
                                    >
                                        <Skeleton height={14} width="4.5rem" />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <tr key={i} className="border-b border-neutral-border/50 bg-white dark:border-white/10 dark:bg-neutral-navy">
                                    {columns.map((column) => (
                                        <td key={column.key} className="min-w-0 px-4 py-3">
                                            <Skeleton height={14} width="70%" />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // -------------------------------------------------------------
    // EMPTY STATES
    // -------------------------------------------------------------
    if (data.length === 0) {
        if (viewMode === "grid" || viewMode === "list") {
            return (
                <div className="rounded-2xl border border-neutral-border/60 bg-white p-12 text-center text-sm text-neutral-secondary dark:border-white/10 dark:bg-neutral-navy">
                    {emptyMessage}
                </div>
            );
        }

        return (
            <div className="overflow-x-auto rounded-mz-xl border border-neutral-border/60 dark:border-white/10">
                <table className={clsx("w-full border-collapse", className)}>
                    <thead>
                        <tr className="border-b border-neutral-border dark:border-white/10">
                            {columns.map((column) => (
                                <th
                                    key={column.key}
                                    className={clsx(
                                        "px-4 py-3 text-start text-sm font-semibold text-neutral-dark dark:text-white",
                                        column.className,
                                    )}
                                >
                                    {column.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td
                                colSpan={columns.length}
                                className="px-4 py-8 text-center text-sm text-neutral-secondary"
                            >
                                {emptyMessage}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }

    // -------------------------------------------------------------
    // GRID VIEW MODE
    // -------------------------------------------------------------
    if (viewMode === "grid") {
        const actionCol = columns.find(
            (c) => c.key.toLowerCase().includes("action") || c.key.toLowerCase().includes("menu"),
        );
        const nonActionCols = columns.filter((c) => c !== actionCol);
        const primaryCol = nonActionCols[0];
        const secondaryCols = nonActionCols.slice(1);

        return (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {data.map((item) => {
                    if (renderGridItem) {
                        return <React.Fragment key={item.id}>{renderGridItem(item)}</React.Fragment>;
                    }

                    return (
                        <div
                            key={item.id}
                            onClick={() => onRowClick?.(item)}
                            className={clsx(
                                "group relative flex flex-col justify-between rounded-2xl border border-neutral-border/60 bg-white p-5 transition-all dark:border-white/10 dark:bg-neutral-navy",
                                onRowClick && "cursor-pointer hover:border-gold/50 hover:shadow-md",
                            )}
                        >
                            <div className="space-y-3">
                                {/* Header section: primary field & optional action menu */}
                                <div className="flex items-start justify-between gap-2 border-b border-neutral-border/40 pb-3 dark:border-white/10">
                                    <div className="min-w-0 flex-1">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-secondary">
                                            {primaryCol?.header}
                                        </span>
                                        <div className="mt-1 font-semibold text-neutral-dark dark:text-white">
                                            {primaryCol?.render
                                                ? primaryCol.render(item)
                                                : ((item as unknown as Record<string, unknown>)[primaryCol?.key] as React.ReactNode)}
                                        </div>
                                    </div>
                                    {actionCol && (
                                        <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                                            {actionCol.render?.(item)}
                                        </div>
                                    )}
                                </div>

                                {/* Body attributes in clean responsive 2-column key-values */}
                                <div className="grid grid-cols-2 gap-3 pt-1">
                                    {secondaryCols.map((col) => (
                                        <div key={col.key} className="min-w-0">
                                            <span className="block text-[11px] font-medium text-neutral-secondary">
                                                {col.header}
                                            </span>
                                            <div className="mt-0.5 truncate text-sm font-medium text-neutral-dark dark:text-neutral-light">
                                                {col.render
                                                    ? col.render(item)
                                                    : ((item as unknown as Record<string, unknown>)[col.key] as React.ReactNode)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    // -------------------------------------------------------------
    // LIST VIEW MODE
    // -------------------------------------------------------------
    if (viewMode === "list") {
        const actionCol = columns.find(
            (c) => c.key.toLowerCase().includes("action") || c.key.toLowerCase().includes("menu"),
        );
        const dataCols = columns.filter((c) => c !== actionCol);

        return (
            <div className="space-y-3">
                {data.map((item) => {
                    if (renderListItem) {
                        return <React.Fragment key={item.id}>{renderListItem(item)}</React.Fragment>;
                    }

                    return (
                        <div
                            key={item.id}
                            onClick={() => onRowClick?.(item)}
                            className={clsx(
                                "group flex flex-wrap items-center justify-between gap-4 rounded-xl border border-neutral-border/60 bg-white p-4 transition-all dark:border-white/10 dark:bg-neutral-navy",
                                onRowClick && "cursor-pointer hover:border-gold/50 hover:shadow-sm",
                            )}
                        >
                            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-6">
                                {dataCols.map((col, idx) => (
                                    <div
                                        key={col.key}
                                        className={clsx("min-w-0", idx === 0 ? "min-w-[160px] flex-1" : "min-w-[100px]")}
                                    >
                                        <span className="block text-[10px] font-bold uppercase tracking-wider text-neutral-secondary">
                                            {col.header}
                                        </span>
                                        <div
                                            className={clsx(
                                                "mt-0.5 text-sm",
                                                idx === 0
                                                    ? "font-semibold text-neutral-dark dark:text-white"
                                                    : "text-neutral-dark/80 dark:text-neutral-light",
                                            )}
                                        >
                                            {col.render
                                                ? col.render(item)
                                                : ((item as unknown as Record<string, unknown>)[col.key] as React.ReactNode)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {actionCol && (
                                <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
                                    {actionCol.render?.(item)}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    }

    // -------------------------------------------------------------
    // DEFAULT TABLE VIEW MODE
    // -------------------------------------------------------------
    return (
        <div className="overflow-x-auto rounded-mz-xl border border-neutral-border/60 dark:border-white/10">
            <table className={clsx("w-full border-collapse", className)}>
                <thead>
                    <tr className="border-b border-neutral-border bg-neutral-light/50 dark:border-white/10 dark:bg-black/20">
                        {columns.map((column) => (
                            <th
                                key={column.key}
                                className={clsx(
                                    "px-4 py-3 text-start text-sm font-semibold text-neutral-dark dark:text-white",
                                    column.className,
                                )}
                            >
                                {column.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((item) => (
                        <tr
                            key={item.id}
                            onClick={() => onRowClick?.(item)}
                            className={clsx(
                                "border-b border-neutral-border/50 dark:border-white/10",
                                onRowClick && "cursor-pointer hover:bg-neutral-light/60 dark:hover:bg-white/5",
                            )}
                        >
                            {columns.map((column) => (
                                <td
                                    key={column.key}
                                    className={clsx("px-4 py-3 text-sm text-neutral-dark dark:text-neutral-light", column.className)}
                                >
                                    {column.render ? column.render(item) : ((item as unknown as Record<string, unknown>)[column.key] as React.ReactNode)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
